// hooks/bid_wizard/useBidEngagements.js

import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import apiService from '../../services/api';

export const useBidEngagements = ({
  engagementType = null,
  customerId = null,
  isActive = null,
  limit = 50,
  enabled = true
} = {}) => {
  return useQuery({
    queryKey: ['bid-engagements', { engagementType, customerId, isActive, limit }],
    queryFn: async () => {
      const params = {};
      if (engagementType) params.engagement_type = engagementType;
      if (customerId)     params.customer_id     = customerId;
      if (isActive !== null) params.is_active    = isActive;
      if (limit)          params.limit           = limit;
      const response = await apiService.get('/engagements', params);
      return response?.data || [];
    },
    enabled,
    staleTime: 5 * 60 * 1000,
    gcTime:   10 * 60 * 1000,
  });
};

export const useBidEngagementDetail = (engagementId, enabled = true) => {
  return useQuery({
    queryKey: ['bid-engagement-detail', engagementId],
    queryFn: async () => {
      const response = await apiService.get(`/engagements/${engagementId}`);
      return response?.data || null;
    },
    enabled: enabled && !!engagementId,
    staleTime: 5 * 60 * 1000,
    gcTime:   10 * 60 * 1000,
  });
};

/**
 * Fetches engagement components (monthly × role × product_service breakdown).
 *
 * Returns { components, monthly_revenue } where:
 *   components      — each row has month, employee_name, employee_role,
 *                     product_service, hours, rate, cost, revenue (cost-share distributed)
 *   monthly_revenue — raw monthly revenue rows from pulse_engagement_monthly_revenue
 *
 * Revenue is distributed proportionally by cost share within each month so
 * buildAnalysis / componentsToServices can sum correctly.
 *
 * Caching strategy:
 *   staleTime: 10 min — modal opens are instant; data served from cache
 *   gcTime:    30 min — kept in memory across navigation
 *   Sync button in ActualsSection invalidates this query key to force a fresh fetch
 *   when the user explicitly requests updated data from QuickBooks.
 */
export const useBidEngagementComponents = ({
  engagementId,
  month        = null,
  employeeRole = null,
  enabled      = true
} = {}) => {
  return useQuery({
    queryKey: ['bid-engagement-components', engagementId, month, employeeRole],
    queryFn: async () => {
      if (!engagementId) return { components: [], monthly_revenue: [] };

      const params = {};
      if (month)        params.month         = month;
      if (employeeRole) params.employee_role = employeeRole;

      const response = await apiService.get(`/engagements/${engagementId}/components`, params);

      const components     = response?.components     || [];
      const monthlyRevenue = response?.monthly_revenue || [];

      // month → total revenue lookup
      const revenueByMonth = {};
      monthlyRevenue.forEach((r) => {
        const key = r.month?.slice(0, 7); // 'YYYY-MM'
        revenueByMonth[key] = (revenueByMonth[key] || 0) + (r.revenue || 0);
      });

      // Distribute revenue by cost share within each month
      const monthTotalCost = {};
      components.forEach((c) => {
        const key = c.month?.slice(0, 7);
        monthTotalCost[key] = (monthTotalCost[key] || 0) + (c.cost || 0);
      });

      const merged = components.map((c) => {
        const key       = c.month?.slice(0, 7);
        const monthRev  = revenueByMonth[key]  || 0;
        const totalCost = monthTotalCost[key]  || 0;
        const costShare = totalCost > 0 ? (c.cost || 0) / totalCost : 0;
        return {
          ...c,
          revenue: monthRev * costShare,
        };
      });

      return {
        components:      merged,
        monthly_revenue: monthlyRevenue,
      };
    },
    enabled: enabled && !!engagementId,
    staleTime: 10 * 60 * 1000, // 10 minutes — instant modal opens from cache
    gcTime:    30 * 60 * 1000, // 30 minutes — survives navigation between engagements
  });
};

/**
 * Convenience hook — role breakdown derived from engagement components.
 *
 * FIX: useBidEngagementComponents returns { components, monthly_revenue }.
 * This hook now correctly reads rawData?.components instead of treating
 * the return value as a flat array.
 */
export const useBidEngagementRoleBreakdown = (engagementId, enabled = true) => {
  const { data: rawData, ...query } = useBidEngagementComponents({ engagementId, enabled });

  const roleBreakdown = useMemo(() => {
    const components = rawData?.components || [];
    if (!components.length) return null;

    const breakdown = {};
    components.forEach((comp) => {
      const role = comp.employee_role || 'Unknown';
      if (!breakdown[role]) {
        breakdown[role] = {
          role,
          total_hours:    0,
          total_cost:     0,
          avg_rate:       0,
          employee_count: new Set(),
          months:         new Set(),
        };
      }
      breakdown[role].total_hours += comp.hours || 0;
      breakdown[role].total_cost  += comp.cost  || 0;
      if (comp.employee_name) breakdown[role].employee_count.add(comp.employee_name);
      if (comp.month)         breakdown[role].months.add(comp.month);
    });

    Object.values(breakdown).forEach((r) => {
      r.avg_rate      = r.total_hours > 0 ? r.total_cost / r.total_hours : 0;
      r.employee_count = r.employee_count.size;
      r.months         = r.months.size;
    });

    return Object.values(breakdown).sort((a, b) => b.total_hours - a.total_hours);
  }, [rawData]);

  return { ...query, roleBreakdown };
};

// ─── NOTE FOR consumers ───────────────────────────────────────────────────────
// useBidEngagementComponents returns { components, monthly_revenue } — NOT a
// flat array. All consumers must use the appropriate key:
//
// BidWizard.jsx useEffect:
//   componentsToServices(templateComponents?.components, ...)
//
// PricingSection / ActualsSection (EngagementModal):
//   rawData?.components      → component rows
//   rawData?.monthly_revenue → revenue rows
//
// ActualsDetailModal:
//   rawData?.components      → component rows
//   rawData?.monthly_revenue → revenue rows
//
// EngagementBidIntelligence:
//   rawData?.components      → used for all analysis (FIXED)
//
// Cache invalidation:
//   After sync: queryClient.invalidateQueries({ queryKey: ['bid-engagement-components', engagementId] })
//   This forces a fresh fetch on next access while keeping cache for all other engagements intact.